import type { FC } from "react";
import { useMemo } from "react";

import type { HistoryAttribute } from "@/types/api/history";

type ChartTooltipComponentProps = {
  attribute: HistoryAttribute;
  value: number;
  date: Date | undefined;
  threshold: number;
};

// Color constants of chart
const COLORS = {
  GREEN: "#17A600",
  ORANGE: "#FFA500",
  RED: "#D20000",
} as const;

// Threshold tolerance
const THRESHOLD_TOLERANCE = 0.1;

const getLabel = (value: number, attribute: HistoryAttribute): string => {
  switch (attribute) {
    case "CIGARETTES":
      return `Cigarettes ${value}/jour`;
    case "DIABETES":
      return `HbA1c ${value}%`;
    case "STRESS":
      return `Stress ${value}/10`;
    case "IP":
      return `IP ${value}%`;
    case "IS":
      return `IS ${value}%`;
    default:
      return value.toString();
  }
};

const getBackgroundColor = (value: number, threshold: number): string => {
  const diff = Math.abs(value - threshold);
  const tolerance = threshold * THRESHOLD_TOLERANCE;

  if (value < threshold && diff > tolerance) {
    return COLORS.GREEN;
  } else if (value > threshold && diff > tolerance) {
    return COLORS.RED;
  } else {
    return COLORS.ORANGE;
  }
};

const ChartTooltipComponent: FC<ChartTooltipComponentProps> = ({
  attribute,
  value,
  threshold,
  date,
}) => {
  const { label, backgroundColor, formattedDate } = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const month = date ? date.getMonth() : new Date().getMonth();
    const dateToDisplay = new Date(currentYear, month, 1);

    return {
      label: getLabel(value, attribute),
      backgroundColor: getBackgroundColor(value, threshold),
      formattedDate: dateToDisplay.toLocaleDateString("fr-FR", {
        month: "long",
        year: "numeric",
      }),
    };
  }, [value, attribute, threshold, date]);

  return (
    <div
      className="flex flex-col items-center gap-0.5 rounded-md px-2 py-1 text-white"
      style={{ backgroundColor }}
    >
      <span className="text-md uppercase">{formattedDate}</span>
      <span className="text-md font-medium">{label}</span>
    </div>
  );
};

export default ChartTooltipComponent;
